'use client';
import { config } from '@/apollo/config';
import { CustomRadioButton } from '@/components';
import {
  ExternalPartnerNameEnum,
  PaymentTabEnum,
  ProductQuery,
  ProductVariantQuery,
  useDefaultChannelQuery,
  useDraftOrderCreateMutation,
} from '@/graphql';
import { useRouter } from '@/i18n/navigation';
import useUserStore from '@/zustand/useUser';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import { BsChevronDown } from 'react-icons/bs';
import { toast } from 'react-toastify';
import { Button, SelectPicker } from 'rsuite';
import styles from './installments.module.scss';

type Props = {
  variantData: ProductVariantQuery | undefined;
  productData: ProductQuery | undefined;
};

type InstallmentOption = {
  __typename: string;
  partner: {
    __typename: string;
    id: string;
    name: string;
    logo: any | null;
    partnerName: string;
    isActive: boolean | null;
    sortOrder: number | null;
    isOwn: boolean | null;
    externalPartnerName?: ExternalPartnerNameEnum | null;
  };
  months: Array<{
    __typename: string;
    monthsAmount: number | null;
    totalPrice: {
      __typename: string;
      currency: string;
      amount: number;
    } | null;
  } | null> | null;
} | null;

const CustomArrow = () => {
  return <BsChevronDown size={20} color="#333" />;
};

const Installments = (props: Props) => {
  const { variantData, productData } = props;
  const t = useTranslations('Components.installments');
  const { user } = useUserStore();
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: channelData } = useDefaultChannelQuery();
  const [createDraftOrder] = useDraftOrderCreateMutation();

  const installmentOptions =
    variantData?.productVariant?.channelListings?.[0]?.installment || [];
  const [selectedMonths, setSelectedMonths] = useState<{
    [key: string]: number | undefined;
  }>({});
  const [selectedPartner, setSelectedPartner] = useState(
    installmentOptions[0]?.partner?.id || null
  );

  const handleMonthChange = (monthAmount: number | null, partnerId: string) => {
    setSelectedMonths(prevState => {
      const newState = { ...prevState };
      if (monthAmount === undefined || monthAmount === null) {
        delete newState[partnerId];
      } else {
        newState[partnerId] = monthAmount;
      }
      return newState;
    });
  };

  const calculatePrice = (
    totalPrice: number | undefined,
    monthsAmount: number | null | undefined
  ) => {
    if (!totalPrice || !monthsAmount) return '0';
    const pricePerMonth = totalPrice / monthsAmount;
    return pricePerMonth.toLocaleString(undefined, {
      minimumFractionDigits: 0,
    });
  };

  const radiusInstallmentPrice = (installments: InstallmentOption[]) => {
    return installments.map(option => {
      if (
        option?.partner.name === 'Radius' &&
        (productData?.product?.defaultVariant?.pricing?.installmentPrice4
          ?.amount ||
          productData?.product?.defaultVariant?.pricing?.installmentPrice6
            ?.amount ||
          productData?.product?.defaultVariant?.pricing?.installmentPrice12
            ?.amount)
      ) {
        const updatedMonths = option.months?.map(month => {
          if (!month || !month.totalPrice) return month;

          const updatedTotalPrice = { ...month.totalPrice };

          if (month.monthsAmount === 4) {
            updatedTotalPrice.amount =
              productData?.product?.defaultVariant?.pricing?.installmentPrice4
                ?.amount ?? 0;
          }
          if (month.monthsAmount === 6) {
            updatedTotalPrice.amount =
              productData?.product?.defaultVariant?.pricing?.installmentPrice6
                ?.amount ?? 0;
          }
          if (month.monthsAmount === 12) {
            updatedTotalPrice.amount =
              productData?.product?.defaultVariant?.pricing?.installmentPrice12
                ?.amount ?? 0;
          }

          return {
            ...month,
            totalPrice: updatedTotalPrice,
          };
        });

        return {
          ...option,
          months: updatedMonths,
        };
      }

      return option;
    });
  };

  const newInstallments = radiusInstallmentPrice(installmentOptions);

  const handleSubmit = async () => {
    if (!selectedPartner) {
      return;
    }

    const partnerOption = newInstallments.find(
      opt => opt?.partner.id === selectedPartner
    );

    if (
      !partnerOption ||
      !partnerOption.months ||
      partnerOption.months.length === 0
    ) {
      return;
    }

    const defaultMonthValue = partnerOption.months[0]?.monthsAmount;
    const monthForSelectedPartner =
      selectedMonths[selectedPartner] ?? defaultMonthValue;

    const variantId = variantData?.productVariant?.id;
    if (!variantId) {
      return;
    }

    const channelId = channelData?.channel?.id;
    if (!channelId) {
      return;
    }

    const userId = user?.id;
    if (!userId) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createDraftOrder({
        variables: {
          input: {
            user: userId,
            lines: [
              {
                quantity: 1,
                variantId: variantId,
              },
            ],
            channelId: channelId,
            isFastBuy: true,
            ...(selectedPartner && {
              installmentPartnerName: partnerOption.partner.externalPartnerName,
              installmentPeriod: monthForSelectedPartner,
              paymentMethodName: PaymentTabEnum.INSTALLMENT,
            }),
          },
        },
      });

      if (result.data?.draftOrderCreate?.order?.id) {
        router.push(`/order`);
      } else {
        toast.error(
          `Ошибка при создании заказа (логическая):, ${result.errors} || Нет данных заказа`
        );
      }
    } catch (error) {
      toast.error(`Ошибка при выполнении мутации createDraftOrder:, ${error}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {newInstallments?.length > 0 && (
        <div className={styles.installmentsWrapper}>
          {/* <div className={styles.header}>
            <div className={styles.div1}>{t('installment')}</div>
          </div> */}
          <div className={styles.items}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div className={styles.container}>
                  <div className={styles.div2}>{t('partners')}</div>
                </div>
                <div className={styles.container}>
                  <div className={styles.div2}>{t('period')}</div>
                </div>
                <div className={styles.container}>
                  <div className={styles.div2}>{t('per-month')}</div>
                </div>
                <div className={styles.container}>
                  <div className={styles.div2}>{t('total')}</div>
                </div>
              </div>
              {newInstallments.map((option, index) => {
                if (!option) {
                  return null;
                }
                const { partner, months } = option;
                const partnerId = partner?.id;

                const monthsData =
                  months?.map(month => {
                    return {
                      label: `${month?.monthsAmount} ${t('months')}`,
                      value: month?.monthsAmount,
                      totalPrice: month?.totalPrice?.amount,
                    };
                  }) || [];

                if (monthsData.length === 0) {
                  return null;
                }

                const defaultMonth = monthsData[0]?.value;
                const selectedMonth = selectedMonths[partnerId] || defaultMonth;

                const selectedMonthData = monthsData.find(
                  month => month.value === selectedMonth
                );

                const totalPrice = selectedMonthData?.totalPrice;
                const perMonth = calculatePrice(totalPrice, selectedMonth);
                return (
                  <div
                    key={index}
                    className={styles.row1}
                    onClick={() => setSelectedPartner(partnerId)}
                  >
                    <div className={styles.nameParent}>
                      <div className={styles.name}>
                        <div className={styles.nameInner}>
                          <CustomRadioButton
                            label={partner.logo ? '' : partner?.name}
                            checked={selectedPartner === partnerId}
                            onChange={() => setSelectedPartner(partnerId)}
                            value={partnerId}
                          />
                          {partner?.logo && (
                            <Image
                              src={`${config.publicApiUrl}${partner.logo}`}
                              alt={partner?.name}
                              width={99}
                              height={32}
                              objectFit="contain"
                              className={styles.partner_img}
                            />
                          )}
                        </div>
                      </div>
                      <div className={styles.parent}>
                        <SelectPicker
                          data={monthsData}
                          caretAs={CustomArrow}
                          appearance="subtle"
                          defaultValue={defaultMonth}
                          cleanable={false}
                          searchable={false}
                          onChange={value =>
                            handleMonthChange(value, partnerId)
                          }
                          value={selectedMonth}
                          style={{ width: 150 }}
                        />
                      </div>
                    </div>
                    <div className={styles.frameParent}>
                      <div className={styles.wrapper1}>
                        <div className={styles.uzumNasiya}>
                          {perMonth} {t('price-month')}
                        </div>
                      </div>
                      <div
                        className={`${styles.wrapper1} ${
                          selectedPartner === partnerId ? styles.active : ''
                        }`}
                      >
                        <div className={styles.uzumNasiya}>
                          {totalPrice?.toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                          })}{' '}
                          {t('price')}
                        </div>
                        {selectedPartner === partnerId && (
                          <Button className={styles.buttonConfirm}>
                            {t('formalize')}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.mobileVersion}>
              {installmentOptions.map((option, index) => {
                if (!option) {
                  return null;
                }
                const { partner, months } = option;
                const partnerId = partner?.id;

                const monthsData =
                  months?.map(month => ({
                    label: `${month?.monthsAmount} ${t('months')}`,
                    value: month?.monthsAmount,
                    totalPrice: month?.totalPrice?.amount,
                  })) || [];

                if (monthsData.length === 0) {
                  return null;
                }

                const defaultMonth = monthsData[0]?.value;
                const selectedMonth = selectedMonths[partnerId] || defaultMonth;

                const selectedMonthData = monthsData.find(
                  month => month.value === selectedMonth
                );

                const totalPrice = selectedMonthData?.totalPrice;
                const perMonth = calculatePrice(totalPrice, selectedMonth);

                return (
                  <div
                    key={index}
                    className={styles.card}
                    onClick={() => setSelectedPartner(partnerId)}
                  >
                    <div className={styles.radioButton}>
                      <div>
                        <CustomRadioButton
                          label={partner.logo ? '' : partner?.name}
                          checked={selectedPartner === partnerId}
                          onChange={() => setSelectedPartner(partnerId)}
                          value={partnerId}
                        />
                        {partner?.logo && (
                          <Image
                            src={`${config.publicApiUrl}${partner.logo}`}
                            alt={partner?.name}
                            width={99}
                            height={32}
                            objectFit="contain"
                            className={styles.partner_img}
                          />
                        )}
                      </div>
                      {selectedPartner === partnerId && (
                        <Button
                          className={styles.buttonConfirm}
                          onClick={() => handleSubmit()}
                          disabled={isSubmitting}
                          loading={isSubmitting}
                        >
                          {t('formalize')}
                        </Button>
                      )}
                    </div>
                    <div className={styles.item}>
                      <div className={styles.title}>Срок</div>
                      <div className={styles.select}>
                        <SelectPicker
                          data={monthsData}
                          caretAs={CustomArrow}
                          appearance="subtle"
                          defaultValue={defaultMonth}
                          cleanable={false}
                          searchable={false}
                          onChange={value =>
                            handleMonthChange(value, partnerId)
                          }
                          value={selectedMonth}
                          style={{ width: 150 }}
                        />
                      </div>
                    </div>
                    <div className={styles.item}>
                      <div className={styles.title}>{t('per-month')}</div>
                      <p className={styles.price}>
                        {perMonth} {t('price-month')}
                      </p>
                    </div>
                    <div className={styles.item}>
                      <div className={styles.title}>{t('total')}</div>
                      <p className={styles.price}>
                        {totalPrice?.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                        })}{' '}
                        {t('price')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Installments;
